import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import omitBy from 'lodash/omitBy';
import uniqBy from 'lodash/uniqBy';
import { useRouter } from 'next/router';

import {
  EXPIRY_TIME,
  LANGUAGE_LAST_ACCESSED,
  LANGUAGE_PREFERRED,
  OB_VISITOR_ID,
  OB_VISITOR_LOCATION,
  OB_VISITOR_REQ_ID,
  OB_VISITOR_IDENTITY,
} from 'constants/cookieConstants';
import { allLanguageCodes, primaryLanguageCodes } from 'constants/languageCodes';

import { getCookie, getVisitorLocationCookie, setCookie, setVisitorLocationCookie } from 'utils/cookie';
import { sentryCaptureException } from 'utils/globals/sentry';
import { useVisitorLocation } from 'utils/hooks/useLocationFilter';
import obRouter from 'utils/operabase-router-client';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Accounts/queries';
import globalQueries from 'containers/Globals/queries';

import { defaultLanguage, i18n } from 'src/i18n';

import { getURLPathAndQuery } from 'operabase-router/utils';

const useAndSaveVisitorLocation = (visitorData, hasFingerprintFailed = false, serverCookies = {}) => {
  const visitorId = visitorData?.visitorId;
  const savedVisitorID = getCookie(OB_VISITOR_ID) || serverCookies[OB_VISITOR_ID];
  const {
    city: savedCity,
    country: savedCountry,
    region: savedRegion,
  } = getVisitorLocationCookie(serverCookies[OB_VISITOR_LOCATION]);

  const { shouldFetchLocationDetails, isReady } = useMemo(() => {
    const isMissingSavedLocation = !savedCity?.id && !savedCountry?.id && !savedRegion?.id;

    if (!visitorId) {
      return {
        shouldFetchLocationDetails: false,
        isReady: !isMissingSavedLocation || hasFingerprintFailed,
      };
    }

    const isVisitorIDChanged = !savedVisitorID || visitorId !== savedVisitorID;

    return {
      shouldFetchLocationDetails: isVisitorIDChanged || isMissingSavedLocation,
      isReady: !isMissingSavedLocation,
    };
  }, [savedCity?.id, savedCountry?.id, savedRegion?.id, savedVisitorID, visitorId, hasFingerprintFailed]);

  const ipLocation = useMemo(() => {
    const {
      city: ipCity,
      country: ipCountry,
      continent: ipContinent,
      subdivisions: _subdivisions,
      ...rest
    } = visitorData?.ipLocation || {};

    return {
      ...rest,
      city: ipCity?.name,
      country: ipCountry?.name,
      continent: ipContinent?.name,
    };
  }, [visitorData?.ipLocation]);

  const { city, country, region, isFetched } = useVisitorLocation(ipLocation, {
    enabled: shouldFetchLocationDetails,
  });

  useEffect(() => {
    if (isFetched) {
      setVisitorLocationCookie({ city, country, region });
    }
  }, [city, country, region, isFetched]);

  if (shouldFetchLocationDetails) {
    return { ipLocation, city, country, region, isReady: isFetched, fromCookies: false };
  }

  return {
    ipLocation,
    city: savedCity,
    country: savedCountry,
    region: savedRegion,
    isReady,
    fromCookies: true,
  };
};

const useLanguageSwitchModal = ({ isTrustedBot, language, serverCookies }) => {
  const lastAccessedLanguage = serverCookies[LANGUAGE_LAST_ACCESSED] || null;
  const preferredLanguage = getCookie(LANGUAGE_PREFERRED) || serverCookies[LANGUAGE_PREFERRED] || null;
  const [isLanguageSwitchModalOpen, setIsLanguageSwitchModalOpen] = useState(false);

  const getBrowserLanguages = useCallback(() => {
    if (typeof window === 'undefined') {
      return [];
    }

    const codes = window?.navigator?.languages.map(lang => lang.split('-')[0]) || [];

    return [...new Set(codes)];
  }, []);

  useEffect(() => {
    if (isTrustedBot || language === 'keys') {
      return;
    }

    const isFirstRender = !lastAccessedLanguage;

    if (isFirstRender && typeof window !== 'undefined') {
      const browserLanguages = getBrowserLanguages();

      if (!browserLanguages.includes(language)) {
        setIsLanguageSwitchModalOpen(true);
      }
    } else if (language !== lastAccessedLanguage && language !== preferredLanguage) {
      setIsLanguageSwitchModalOpen(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSetPreferredLanguage = useCallback(langIso => {
    setCookie(LANGUAGE_PREFERRED, langIso, new Date(new Date().getTime() + EXPIRY_TIME.YEAR));
  }, []);

  const getSuggestedLanguages = useCallback(() => {
    if (typeof window === 'undefined') {
      return primaryLanguageCodes;
    }

    const browserLanguages = getBrowserLanguages();
    const userLanguageCodes = [
      ...new Set([preferredLanguage, lastAccessedLanguage, ...browserLanguages].filter(Boolean)),
    ];
    const userPreferredLanguageOptions = userLanguageCodes.reduce((acc, lang) => {
      const matchedLanguage = allLanguageCodes.find(l => l.iso === lang);

      if (matchedLanguage) {
        acc.push(matchedLanguage);
      }

      return acc;
    }, []);

    const [englishOption, ...ourSuggestedLanguages] = primaryLanguageCodes;

    const validLanguages = uniqBy(
      [englishOption, ...userPreferredLanguageOptions, ...ourSuggestedLanguages],
      'iso',
    ).filter(Boolean);

    return validLanguages.slice(0, 5);
  }, [lastAccessedLanguage, preferredLanguage, getBrowserLanguages]);

  return {
    preferredLanguage,
    lastAccessedLanguage,
    getSuggestedLanguages,
    isOpen: isLanguageSwitchModalOpen,
    onSetPreferredLanguage,
  };
};

const AppContext = createContext({});

const useObRouteContext = ({ language, serverRouteContext, userId }) => {
  const [obRouteContext, setObRouteContext] = useState({ ...serverRouteContext, source: 'server' });
  const permissionResponse = useQuery(queries.getUserPermissions({ id: userId, queryConfig: { enabled: false } }));
  const router = useRouter();
  const { asPath, query } = router;

  const { changed, next } = useMemo(
    () => obRouter.hasURLChanged({ current: obRouteContext?.url, next: asPath }),
    [obRouteContext?.url, asPath],
  );

  const getClientPermissions = useCallback(() => permissionResponse?.data || {}, [permissionResponse]);

  useEffect(() => {
    if (changed) {
      if (serverRouteContext?.nextdata && serverRouteContext?.url === next) {
        setObRouteContext({ ...serverRouteContext, source: 'next-data', oldState: obRouteContext });
      } else {
        const [path] = getURLPathAndQuery(next);

        obRouter
          .getRequestContext({
            lng: language,
            path,
            query,
            originalUrl: next,
            previousEntityMap: obRouteContext?.entityMap,
            previousPageInfo: obRouteContext?.page,
            previousFilterParams: obRouteContext?.filterParams,
            getClientPermissions,
          })
          .then(([state]) => {
            setObRouteContext({ ...state, source: 'client', oldState: obRouteContext });
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obRouteContext, asPath, query, language, serverRouteContext, changed, next]);

  const finalObRouteContext = useMemo(
    () => ({
      ...obRouteContext,
      isReady: !changed,
    }),
    [obRouteContext, changed],
  );

  return finalObRouteContext;
};

const useVisitorIdentity = ({ isLoggedIn, visitorId, obRouteContext }) => {
  const { identity } = obRouteContext;

  const shouldFetchUserFingerprintData = !isLoggedIn && !!visitorId && !identity?.user?.id;

  const { data: userInfo } = useQuery(
    globalQueries.getUserByVisitorId({
      id: visitorId,
      extraQueryKeys: [isLoggedIn],
      queryConfig: {
        enabled: shouldFetchUserFingerprintData,
        ...(!shouldFetchUserFingerprintData &&
          identity?.user?.id && {
            initialData: {
              data: [
                {
                  user: identity?.user,
                },
              ],
            },
          }),
        select: response => {
          const { id, email, auth_via, loginType, name } = response?.data?.[0]?.user || {};

          return {
            id,
            email,
            loginType: loginType || auth_via,
            name,
          };
        },
        onSuccess: response => {
          const { id, email, loginType, name } = response || {};

          if (id) {
            setCookie(OB_VISITOR_IDENTITY, JSON.stringify({ id, email, loginType, name }));
          }
        },
      },
    }),
  );

  return userInfo;
};

export const AppContextProvider = ({
  children,
  sessionUser,
  locale,
  serverCookies,
  serverRouteContext,
  fingerprintContext,
}) => {
  const { visitorInfo, isTrustedBot } = fingerprintContext || {};
  const language = useMemo(() => {
    const serverLanguage = locale || defaultLanguage;

    if (typeof window === 'undefined') {
      return serverLanguage?.toLowerCase();
    }

    return (i18n?.language || serverLanguage)?.toLowerCase();
  }, [locale]);

  const [storeUserId, setLoggedInUserId] = useState(sessionUser?.id);
  const obRouteContext = useObRouteContext({ language, serverRouteContext, userId: storeUserId });
  const { data: visitorData, isLoading, error } = visitorInfo || {};
  const hasFingerprintFailed = useMemo(
    () => !isLoading && (!!error || !visitorData?.visitorId),
    [error, isLoading, visitorData?.visitorId],
  );

  const savedVisitorID = getCookie(OB_VISITOR_ID) || serverCookies[OB_VISITOR_ID];
  const savedReqID = getCookie(OB_VISITOR_REQ_ID) || serverCookies[OB_VISITOR_REQ_ID];

  const location = useAndSaveVisitorLocation(visitorData, hasFingerprintFailed, serverCookies);

  const [scrollBarWidth, setScrollBarWidth] = useState(0);
  const [paywall, setPaywall] = useState({ allowFilters: true, collapsible: true });

  const { preferredLanguage, getSuggestedLanguages, ...languageSwitcher } = useLanguageSwitchModal({
    isTrustedBot,
    language,
    serverCookies,
  });

  useEffect(() => {
    if (hasFingerprintFailed) {
      sentryCaptureException(error, { extra: { savedVisitorID } });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFingerprintFailed]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setScrollBarWidth(window.innerWidth - document.body.clientWidth);
    }
  }, []);

  useEffect(() => {
    if ((visitorData?.visitorId && visitorData.visitorId !== savedVisitorID) || visitorData?.requestId !== savedReqID) {
      if (visitorData?.visitorId) {
        setCookie(OB_VISITOR_ID, visitorData?.visitorId);
      }

      if (visitorData?.requestId) {
        setCookie(OB_VISITOR_REQ_ID, visitorData?.requestId);
      }
    }
  }, [visitorData?.visitorId, savedVisitorID, visitorData?.requestId, savedReqID]);

  const visitorId = useMemo(() => visitorData?.visitorId || savedVisitorID, [visitorData?.visitorId, savedVisitorID]);
  const visitorIdentity = useVisitorIdentity({ isLoggedIn: !!storeUserId, visitorId, obRouteContext });

  const appContextValue = useMemo(
    () => ({
      isTrustedBot,
      userId: storeUserId,
      isLoggedIn: !!storeUserId,
      isReturningGuestUser: !storeUserId && !!visitorIdentity?.id,
      setLoggedInUserId,
      language,
      preferredLanguage,
      getSuggestedLanguages,
      scrollBarWidth,
      visitorId,
      requestId: visitorData?.requestId,
      location,
      hasFingerprintFailed,
      languageSwitcher,
      obRouteContext,
      paywall,
      setPaywall,
      visitorIdentity,
    }),
    [
      isTrustedBot,
      storeUserId,
      language,
      preferredLanguage,
      getSuggestedLanguages,
      scrollBarWidth,
      visitorId,
      visitorData?.requestId,
      location,
      hasFingerprintFailed,
      languageSwitcher,
      obRouteContext,
      paywall,
      visitorIdentity,
    ],
  );

  const debugAppContext = useCallback(() => {
    const logger = console;
    const data = omitBy(appContextValue, value => typeof value === 'function');

    logger.log(data);
    return data;
  }, [appContextValue]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugAppContext = debugAppContext;
    }
  }, [debugAppContext]);

  return <AppContext.Provider value={appContextValue}>{children}</AppContext.Provider>;
};

const useAppContext = () => useContext(AppContext);

export default useAppContext;
